import { useEffect } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useInView } from 'react-intersection-observer'
import { getGetProductsByCategoryIdV2QueryKey, getProductsByCategoryIdV2 } from '@/apis/rest/generated/hooks'
import { ProductSortByParamParameter } from '@/apis/rest/generated/types'
import { useBackAction } from '@/components/common/back-action/use-back-action'
import { PAGE_LIMIT } from '@/constants/pages.const'
import { useCustomRouter, usePetType } from '@/containers/hooks'
import { getSortDirectionByOrder, parseSortByOptionSelected } from '@/utils/getSortDirectionByOrder'
import { decodeId } from '@/utils/utilApi'

type UseProductListContainerHookProps = {
  categoryId: string
}

const useCategoriesProductListV2 = ({ categoryId }: UseProductListContainerHookProps) => {
  const { query } = useCustomRouter()
  const { inView, ref } = useInView()
  const { getPetType } = usePetType()
  const backAction = useBackAction('recommend-list-for-category-list')

  // 강아지 > 간식, 강아지 > 용품, 강아지 > 건강관리, 고양이 > 용품 카테고리에서는 리뷰 많은 순으로 정렬 나머지는 인기 많은 순으로 정렬
  const reviewCountFilterCategories = ['dog-treat', 'dog-supplies', 'dog-healthcare-product', 'cat-supplies']
  const isOrderByReviewCount = reviewCountFilterCategories.includes(query?.rootCategorySlug as string)

  const categoryOrderBy = isOrderByReviewCount
    ? ProductSortByParamParameter.REVIEW_COUNT
    : ProductSortByParamParameter.SALES_SCORE

  // url query string에 orderBy가 없다면 최초로 페이지에 진입 한 것 -> 최초 진입 했을때만 품절 상품을 아래쪽으로 정렬 하기로 정책 정해짐
  // https://www.notion.so/industrious-ethernet-850/c6b4de24f3034ae2a483455771dfaed5
  const orderBy = (query?.orderBy as ProductSortByParamParameter) || categoryOrderBy
  const {
    data: productListData,
    isLoading,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery(
    getGetProductsByCategoryIdV2QueryKey(Number(decodeId(categoryId).id), {
      sortBy: parseSortByOptionSelected(orderBy),
      sortDirection: getSortDirectionByOrder(orderBy),
      petType: getPetType(),
      sortSoldOutToLast: true,
    }),
    ({ pageParam = PAGE_LIMIT.DEFAULT_PAGE }) =>
      getProductsByCategoryIdV2(Number(decodeId(categoryId).id), {
        sortBy: parseSortByOptionSelected(orderBy),
        sortDirection: getSortDirectionByOrder(orderBy),
        page: pageParam,
        size: PAGE_LIMIT.LIST,
        includeSoldOut: true,
        sortSoldOutToLast: true,
        petType: getPetType(),
      }),
    {
      getNextPageParam: (lastPage, allPages) => {
        return lastPage.data?.length === PAGE_LIMIT.LIST ? allPages.length + PAGE_LIMIT.DEFAULT_PAGE : undefined
      },
      enabled: !!categoryId,
      onSuccess: (res) => {
        if (res.pages.length <= 1) {
          backAction.clear()
        }
      },
    }
  )

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage()
    }
  }, [inView, fetchNextPage, hasNextPage])

  const listTotalCount = productListData?.pages[0]?.page?.totalCount || 0

  return {
    isLoading,
    productListData,
    orderBy,
    listTotalCount,
    ref,
  }
}

export default useCategoriesProductListV2
